import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Zap, Bike, Wallet, CalendarCheck, Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  name: "", type: "moped", image: "", description: "", engine: "", top_speed: "", range_km: "",
  daily_rate: "", weekly_rate: "", monthly_rate: "", available: true, features: "",
};

const BOOKING_STATUSES = ["pending_payment", "confirmed", "active", "completed", "cancelled"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [bikes, setBikes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/bikes").then((r) => setBikes(r.data));
    api.get("/bookings").then((r) => setBookings(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...b, features: (b.features || []).join(", ") });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      daily_rate: 0,
      weekly_rate: parseFloat(form.weekly_rate),
      monthly_rate: parseFloat(form.monthly_rate),
      features: form.features.split(",").map((f) => f.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/bikes/${editing.id}`, payload);
      else await api.post("/bikes", payload);
      toast.success(editing ? "Bike updated" : "Bike added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bike?")) return;
    await api.delete(`/bikes/${id}`);
    toast.success("Bike deleted");
    load();
  };

  const changeStatus = async (id, status) => {
    await api.put(`/bookings/${id}/status`, { status });
    toast.success("Status updated");
    load();
  };

  const statCards = [
    { icon: Bike, label: "Total bikes", value: stats?.total_bikes ?? "—" },
    { icon: CalendarCheck, label: "Total bookings", value: stats?.total_bookings ?? "—" },
    { icon: Wallet, label: "Paid bookings", value: stats?.paid_bookings ?? "—" },
    { icon: Zap, label: "Revenue", value: stats ? `£${stats.revenue}` : "—" },
  ];

  return (
    <div className="min-h-screen bg-secondary/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between">
          <div>
            <span className="label-caps text-primary">Admin</span>
            <h1 className="mt-2 text-3xl font-black tracking-tight">Control room</h1>
          </div>
          <Link to="/"><Button variant="outline" className="font-semibold hover:text-primary">View site</Button></Link>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-border rounded-xl p-5">
              <s.icon className="w-5 h-5 text-primary" />
              <p className="mt-3 text-3xl font-black font-display">{s.value}</p>
              <p className="label-caps mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bikes" className="mt-10">
          <TabsList>
            <TabsTrigger value="bikes" data-testid="tab-bikes">Bikes ({bikes.length})</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          </TabsList>

          {/* Bikes */}
          <TabsContent value="bikes" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button onClick={openNew} data-testid="add-bike-btn" className="bg-primary hover:bg-[#1D4ED8] text-white font-bold gap-2"><Plus className="w-4 h-4" /> Add bike</Button>
            </div>
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <div className="divide-y divide-border">
                {bikes.map((b) => (
                  <div key={b.id} data-testid={`admin-bike-${b.id}`} className="flex items-center gap-4 p-4">
                    <img src={b.image} alt={b.name} className="w-16 h-16 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold truncate">{b.name}</h3>
                        <Badge className="capitalize bg-secondary text-foreground hover:bg-secondary">{b.type}</Badge>
                        {!b.available && <Badge className="bg-destructive text-white hover:bg-destructive">Hidden</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">£{b.weekly_rate}/wk · £{b.monthly_rate}/mo</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`edit-bike-${b.id}`} className="hover:text-primary"><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(b.id)} data-testid={`delete-bike-${b.id}`} className="hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings" className="mt-6">
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <div className="divide-y divide-border">
                {bookings.length === 0 && <p className="p-6 text-muted-foreground">No bookings yet.</p>}
                {bookings.map((b) => (
                  <div key={b.id} data-testid={`admin-booking-${b.id}`} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4">
                    <div className="flex-1">
                      <h3 className="font-bold">{b.bike_name}</h3>
                      <p className="text-sm text-muted-foreground">{b.user_name} · {b.user_email}</p>
                      <p className="text-sm text-muted-foreground capitalize">{b.plan} × {b.quantity} · {b.start_date} · {b.payment_status === "paid" ? "Paid" : "Unpaid"}</p>
                    </div>
                    <p className="text-xl font-black font-display">£{b.total_amount}</p>
                    <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                      <SelectTrigger className="w-40" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bike form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit bike" : "Add bike"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bike-form-name" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1" data-testid="bike-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="moped">Moped</SelectItem><SelectItem value="motorbike">Motorbike</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Available</Label>
                <Select value={String(form.available)} onValueChange={(v) => setForm({ ...form, available: v === "true" })}>
                  <SelectTrigger className="mt-1" data-testid="bike-form-available"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="bike-form-image" className="mt-1" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="bike-form-description" className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Engine</Label><Input value={form.engine} onChange={(e) => setForm({ ...form, engine: e.target.value })} className="mt-1" /></div>
              <div><Label>Top speed</Label><Input value={form.top_speed} onChange={(e) => setForm({ ...form, top_speed: e.target.value })} className="mt-1" /></div>
              <div><Label>Range</Label><Input value={form.range_km} onChange={(e) => setForm({ ...form, range_km: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Weekly £ (max 80)</Label><Input type="number" max={80} value={form.weekly_rate} onChange={(e) => setForm({ ...form, weekly_rate: e.target.value })} data-testid="bike-form-weekly" className="mt-1" /></div>
              <div><Label>Monthly £</Label><Input type="number" value={form.monthly_rate} onChange={(e) => setForm({ ...form, monthly_rate: e.target.value })} data-testid="bike-form-monthly" className="mt-1" /></div>
            </div>
            <div><Label>Features (comma separated)</Label><Input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="bike-form-features" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} data-testid="bike-form-save" className="bg-primary hover:bg-[#1D4ED8] text-white font-bold">Save bike</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
